/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2019-2022, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/

// Importing our module name from our AngularJS module:
import { TranslocoService } from '@jsverse/transloco';
import { APP_INITIALIZER, DoBootstrap, NgModule } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { UpgradeModule } from '@angular/upgrade/static';
import { DataViewModule } from 'primeng/dataview';
import { MultiSelectModule } from 'primeng/multiselect';
import { providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeng/themes';
import Lara from '@primeng/themes/lara';
import moduleName from '../ajs/app.module.ajs';

// Core Angular Modules:

// Our custom Modules, Services:
import { AeroModule } from '../aero/aero.module';
import { AvatarModule } from './avatar/avatar.module';
import { AssignmentModule } from './assignment/assignment.module';
import { BulbCommonModule } from '../common/bulb-common.module';
import { BulbCkeditorModule } from '../ckeditor/bulb-ckeditor.module';
import { BulbCkeDialogModule } from '../ckeditor/component/dialog/bulb-ckedialog.module';
import { BulbImageCropperModule } from './image-cropper/bulb-image-cropper.module';
import { BulbImageModule } from './image/bulb-image.module';
import { CompositeViewModule } from './composite-view/composite-view.module';
import { BulbGroupInviteModule } from './group-invite-dialog/bulb-group-invite.module';
import { BulbMacmillanModule } from './macmillan-book-dialog/bulb-macmillan.module';
import { DevToolsModule } from './dev-tools/dev-tools.module';
import { FamilyMemberModule } from '../family-member/family-member.module';
import { GettingStartedTemplatesModule } from './getting-started-templates-dialog/getting-started-templates.module';
import { GroupModule } from './group/group.module';
import { ImageCarouselModule } from './image-carousel/image-carousel.module';
import { MobileLauncherModule } from './mobile-launcher/mobile-launcher.module';
import { MenusModule } from './menus/menus.module';
import { OfflineModule } from './offline/offline.module';
import { PageViewFooterModule } from './page-view-footer/page-view-footer.module';
import { PageViewHeaderModule } from './page-view-header/page-view-header.module';
import { PageViewModule } from './page-view/page-view.module';
import { PlanListModule } from './buy-page/plan-list.module';
import { ResumeModule } from '../resume/resume.module';
import { SaveModule } from '../save/save.module';
import { SharePanelModule } from './share-panel/share-panel.module';
import { ShutdownAlertModule } from './alert/shutdown-alert/shutdown-alert.module';
import { AeroDialogsModule } from '../aero/aero-dialogs/aero-dialogs.module';
import { TilesModule } from './tiles/tiles.module';
import { TourModule } from './tour/tour.module';

// Our custom Services:
import { AeroDialogService } from '../aero/aero-dialog/aero-dialog.service';
import { AudioVisualService } from './audio/audio-visual/audio-visual.service';
import { AudioCompatibilityService } from './audio/audio-compatibility.service';
import { BulbFetchMediaDataService } from './audio/fetch-media-data.service';
import { BulbFetchUserService } from '../common/fetch-user.service';
import { FamilyMemberService } from '../family-member/family-member.service';
import { ErrorService } from '../common/error.service';
import { LoggedOutService } from './helpers/logged-out.service';
import { PageSnapshotDialogService } from './page-snapshot-dialog/page-snapshot-dialog.service';

// Our custom Angular Components:
import { AddStudentDialogComponent } from './account-settings-dialog/add-student-dialog/add-student-dialog.component';
import { AudioPlaybackComponent } from './audio/audio-playback/audio-playback.component';
import { AudioRecordComponent } from './audio/audio-record/audio-record.component';
import { DeleteAccountDialogComponent } from './account-settings-dialog/delete-account-dialog/delete-account-dialog.component';
import { PageSnapshotDialogComponent } from './page-snapshot-dialog/page-snapshot-dialog.component';
import { AccountSettingsModule } from './account-settings/account-settings.module';

// Exports and Angular Pipes:
import { fetchCurrentUserServiceFactory } from './exports';

// AngularJS providers needed in Angular
import {
  BulbAssetLibraryServiceProvider,
  BulbAssetLibraryUnsplashServiceProvider,
  BulbAssetLibraryUploadServiceProvider,
  BulbAutoSaveServiceProvider,
  BulbAuthServiceProvider,
  BulbPresentationServiceProvider,
  BulbBusyCoverServiceProvider,
  BulbGroupServiceProvider,
  RootScopeProvider,
  StateProvider,
  AssetLibraryProvider,
} from './angularjs.providers';

// Primeng components
import { TranslocoRootModule } from './transloco/transloco-root.module';

import { UrlPrefixInterceptor } from './helpers/http-url-prefix-interceptor';
import { ErrorInterceptor } from './helpers/http-error-interceptor';
import { LoggedInHeaderInterceptor } from './helpers/http-logged-in-header-interceptor';
import { ShareLinkDialogComponent } from './share-link-dialog/share-link-dialog.component';
import { SearchModule } from './search/search.module';
import { OrganizationModule } from './organization/organization.module';

const MyPreset = definePreset(Lara, {
  components: {
    paginator: {
      colorScheme: {
        light: {
          navButton: {
            selectedBackground: '#6dcff6',
            selectedColor: '#007ad9',
          },
        },
      },
    },
    toast: {
      colorScheme: {
        light: {
          error: {
            borderColor: '#C42D4E',
            color: '#EE315B',
          },
          info: {
            borderColor: '#000060',
            color: '#2574A9',
          },
        },
      },
    },
    multiselect: {
      colorScheme: {
        light: {
          hoverBorderColor: '#000',
          focusBorderColor: '#6dcff6',
          option: {
            groupBackground: '#f4f4f4',
            hoverBackground: '#eaeaea',
            focusBackground: '#eaeaea',

            selectedBackground: 'transparent',
            selectedHoverBackground: 'transparent',
            selectedFocusBackground: 'transparent',

            selectedColor: 'inherit',
            selectedHoverColor: 'inherit',
            selectedFocusColor: 'inherit',
          },
        },
      },
    },
    dialog: {
      content: {
        padding: '1rem',
      },
    },
    popover: {
      content: {
        padding: '0',
      },
    },
    toggleswitch: {
      colorScheme: {
        light: {
          checkedBackground: '#00aeef',
          checkedHoverBackground: '#2574a9',
        },
      },
    },
    select: {
      colorScheme: {
        light: {
          focusRing: {
            width: '0',
            style: 'none',
            color: 'transparent',
            shadow: '0 2px 4px #0003',
          },
          option: {
            focusBackground: '#eaeaea',
            selectedBackground: '#007ad9',
            selectedFocusBackground: '#007ad9',
          },
        },
      },
    },
    inputtext: {
      root: {
        hoverBorderColor: '#000',
        focusBorderColor: '#6dcff6',
        focusRing: {
          width: '0',
          style: 'none',
          color: 'transparent',
          shadow: 'none',
        },
      },
    },
    checkbox: {
      colorScheme: {
        light: {
          root: {
            hoverBorderColor: '#000',
            checkedHoverBackground: '#005b9f',
            checkedHoverBorderColor: '#005b9f',
            checkedBackground: '#007ad9',
            checkedBorderColor: '#fff',
          },
        },
      },
    },
    textarea: {
      root: {
        hoverBorderColor: '#000',
        focusBorderColor: '#6dcff6',
        focusRing: {
          width: '0',
          style: 'none',
          color: 'transparent',
          shadow: '0 2px 4px #0003',
        },
      },
    },
    button: {
      colorScheme: {
        light: {
          primary: {
            hoverBorderColor: 'transparent',
            activeBorderColor: 'transparent',
          },
        },
      },
    },
  },
});

/*
 * Main bulb Angular module, which also bootstraps the angularJs app.
 */
@NgModule({
  declarations: [
    AddStudentDialogComponent,
    AudioPlaybackComponent,
    AudioRecordComponent,
    DeleteAccountDialogComponent,
    PageSnapshotDialogComponent,
    ShareLinkDialogComponent,
  ],
  imports: [AeroModule,
    AccountSettingsModule,
    AvatarModule,
    AssignmentModule,
    BrowserAnimationsModule,
    BulbCommonModule,
    BrowserModule,
    BulbCkeditorModule,
    BulbCkeDialogModule,
    BulbImageCropperModule,
    BulbImageModule,
    CompositeViewModule,
    BulbMacmillanModule,
    BulbGroupInviteModule,
    DataViewModule,
    DevToolsModule,
    FamilyMemberModule,
    FormsModule,
    GettingStartedTemplatesModule,
    ImageCarouselModule,
    MobileLauncherModule,
    MenusModule,
    OfflineModule,
    OrganizationModule,
    PageViewFooterModule,
    PageViewHeaderModule,
    PageViewModule,
    PlanListModule,
    ResumeModule,
    GroupModule,
    SaveModule,
    SvgIconComponent,
    SearchModule,
    SharePanelModule,
    ShutdownAlertModule,
    AeroDialogsModule,
    TilesModule,
    TourModule,
    UpgradeModule,
    TranslocoRootModule,
    MultiSelectModule],
  providers: [
  // Services
    AeroDialogService,
    AudioCompatibilityService,
    AudioVisualService,
    BulbFetchMediaDataService,
    BulbFetchUserService,
    LoggedOutService,
    ErrorService,
    FamilyMemberService,
    PageSnapshotDialogService,
    // Providers
    AssetLibraryProvider,
    BulbAssetLibraryServiceProvider,
    BulbAssetLibraryUnsplashServiceProvider,
    BulbAssetLibraryUploadServiceProvider,
    BulbAutoSaveServiceProvider,
    BulbAuthServiceProvider,
    BulbPresentationServiceProvider,
    BulbBusyCoverServiceProvider,
    BulbGroupServiceProvider,
    RootScopeProvider,
    StateProvider,
    {
      provide: APP_INITIALIZER,
      useFactory: fetchCurrentUserServiceFactory,
      deps: [BulbFetchUserService, TranslocoService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoggedInHeaderInterceptor,
      multi: true,
      deps: [LoggedOutService],
    },
    { provide: HTTP_INTERCEPTORS, useClass: UrlPrefixInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    provideHttpClient(withInterceptorsFromDi()),
    providePrimeNG({
      theme: {
        preset: MyPreset,
      },
    }),
  ],
})
export class AppModule implements DoBootstrap {
  constructor(private upgrade: UpgradeModule) {
    // @ts-ignore
    window.bulbGlobalVars = {};
  }

  ngDoBootstrap() {
    this.upgrade.bootstrap(document.documentElement, [moduleName], { strictDi: true });
  }
}
